import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useRef,
} from "react";
import axios from "axios";

import PropertyPanel from "./PropertyPanel";
import LayersPanel from "./LayersPanel";
import InsertPanel from "./InsertPanel";
import PagesPanel from "./pages-panel";
import { visualEditorService } from "@/lib/visual-editor/visual-editor-service";
import { useToast } from "@/hooks/use-toast";
import { useOnboardingMark } from "@/contexts/onboarding-context";
import { Tabs, TabsContent } from "../ui/tabs";
import { useVisualEditorHistoryStore } from "@/stores/visualEditorHistoryStore";
import { getAllPendingChanges } from "@/lib/utils/visual-editor-history-utils";
import {
  parseSourceLocation,
  getActionType,
  isSectionChild,
  getSectionParentId,
} from "@/lib/visual-editor/helper";
import { isGeneratedId } from "@/lib/visual-editor/generated-id";
import type { PropertyPanelRef } from "@/types/property-panel.types";
import type { HistorySnapshot } from "@/types/visual-editor-history.types";
import type { ElementData } from "@/types/preview-message.types";
import { auth } from "@/lib/firebase";
import {
  PreviewMessageType,
  previewMessage,
} from "@/lib/visual-editor/post-message";
import { usePermissions } from "@/hooks/usePermissions";
import { Eye } from "lucide-react";
import { useAgentChatStore } from "@/stores/agentChatStore";
import { useSlideStore } from "@/stores/slide-store";
import { fetchSlideManifest } from "@/lib/slides/slide-manifest";
import { Commit } from "@optidev/shared-types";

// Constants
const KEYBOARD_SHORTCUTS = {
  UNDO: { key: "z", shift: false },
  REDO: [
    { key: "z", shift: true },
    { key: "y", shift: false },
  ],
} as const;

const LAYERS_UPDATE_DELAY = 100; // ms - delay after reorder to allow DOM to update

// Types
export type VisualEditorTab = "property" | "insert" | "layers" | "pages";

/**
 * Check if keyboard event matches undo shortcut (Ctrl+Z / Cmd+Z)
 */
const isUndoShortcut = (e: KeyboardEvent): boolean => {
  return (
    (e.ctrlKey || e.metaKey) &&
    e.key === KEYBOARD_SHORTCUTS.UNDO.key &&
    !e.shiftKey
  );
};

/**
 * Check if keyboard event matches redo shortcut (Ctrl+Shift+Z / Cmd+Shift+Z or Ctrl+Y / Cmd+Y)
 */
const isRedoShortcut = (e: KeyboardEvent): boolean => {
  return KEYBOARD_SHORTCUTS.REDO.some((shortcut) => {
    return (
      (e.ctrlKey || e.metaKey) &&
      e.key === shortcut.key &&
      e.shiftKey === shortcut.shift
    );
  });
};

/**
 * Find an element in the layers tree by its ID
 * @param layers - The layers array to search
 * @param elementId - The element ID to find
 * @returns Element info with parent and index, or null if not found
 */
const findElementInLayers = (
  layers: any[],
  elementId: string,
): { element: any; parent: any; index: number } | null => {
  for (let i = 0; i < layers.length; i++) {
    // Check if this is the element
    if (layers[i].id === elementId) {
      return {
        element: layers[i],
        parent: null, // Top-level element
        index: i,
      };
    }
    // Check children
    if (layers[i].children && layers[i].children.length > 0) {
      const childIndex = layers[i].children.findIndex(
        (child: any) => child.id === elementId,
      );
      if (childIndex !== -1) {
        return {
          element: layers[i].children[childIndex],
          parent: layers[i],
          index: childIndex,
        };
      }
      // Recursively search in nested children
      const nested = findElementInLayers(layers[i].children, elementId);
      if (nested) return nested;
    }
  }
  return null;
};

interface VisualEditorProps {
  devContainerId?: string;
  projectId?: string;
  previewUrl?: string;
  previewRef?: React.RefObject<HTMLIFrameElement>;
  selectedElement?: ElementData | null;
  multiSelectCount?: number;
  isSelectionEnabled?: boolean;
  onToggleSelection?: (enabled: boolean) => void;
  onElementDeselect?: () => void;
  onCommitStateChange?: (state: "idle" | "generating" | "creating") => void;
  onAiCommand?: (command: string) => void;
  activeTab?: VisualEditorTab;
  onActiveTabChange?: (tab: VisualEditorTab) => void;
  currentPath?: string;
  onPathChange?: (path: string) => void;
}

const VisualEditor = forwardRef<any, VisualEditorProps>(
  (
    {
      devContainerId,
      projectId,
      previewUrl,
      previewRef,
      selectedElement: externalElement,
      multiSelectCount,
      isSelectionEnabled = false,
      onToggleSelection,
      onElementDeselect,
      onCommitStateChange,
      onAiCommand,
      activeTab: externalActiveTab,
      onActiveTabChange,
      currentPath,
      onPathChange,
    },
    ref,
  ) => {
    const [internalSelectedElement, setInternalSelectedElement] =
      useState<ElementData | null>(null);
    const [isPropertyPanelOpen, setIsPropertyPanelOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [layers, setLayers] = useState<any[]>([]);
    const [internalActiveTab, setInternalActiveTab] =
      useState<VisualEditorTab>("property");
    const activeTab = externalActiveTab ?? internalActiveTab;
    const setActiveTab = onActiveTabChange ?? setInternalActiveTab;
    const [pendingChanges, setPendingChanges] = useState<{
      text?: string;
      className?: string;
      styles?: Record<string, string>;
      attributes?: Record<string, string>;
    }>({});
    const [hasInitialState, setHasInitialState] = useState(false);

    // Store the original element state when first selected
    const { toast } = useToast();
    const markFirstEdit = useOnboardingMark();
    const propertyPanelRef = useRef<PropertyPanelRef>(null);
    const originalElementRef = useRef<ElementData | null>(null);

    // Use external element if provided, otherwise use internal state
    const selectedElement = externalElement || internalSelectedElement;

    // History store
    const {
      pushHistory,
      undo,
      redo,
      canUndo,
      canRedo,
      clearHistory,
      history,
      currentIndex,
    } = useVisualEditorHistoryStore();

    // Hook for triggering sync changes detection in PublishDropdown
    const setAutoCommit = useAgentChatStore((s) => s.setAutoCommit);

    // Permissions for viewer role restrictions
    const { canEditFiles, isViewer } = usePermissions();

    /**
     * Update PropertyPanel with pending changes
     */
    const updatePropertyPanel = (pendingChanges: {
      text?: string;
      styles?: Record<string, string>;
      attributes?: Record<string, string>;
    }) => {
      if (!propertyPanelRef.current) return;

      if (pendingChanges.text !== undefined) {
        propertyPanelRef.current.updateContent(pendingChanges.text);
      }
      if (pendingChanges.styles) {
        propertyPanelRef.current.updateStyles(pendingChanges.styles);
      }
    };

    /**
     * Update internal state and PropertyPanel
     */
    const updateElementState = (
      element: ElementData,
      pendingChanges: {
        text?: string;
        styles?: Record<string, string>;
        attributes?: Record<string, string>;
      },
    ) => {
      setInternalSelectedElement(element);
      setPendingChanges(pendingChanges);
      updatePropertyPanel(pendingChanges);
    };

    // Keyboard shortcuts
    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        // Only handle shortcuts when property panel is open
        if (!isPropertyPanelOpen) return;

        // Undo: Ctrl+Z / Cmd+Z
        if (isUndoShortcut(e)) {
          e.preventDefault();
          if (canUndo()) {
            handleUndo();
          }
        }

        // Redo: Ctrl+Shift+Z / Cmd+Shift+Z or Ctrl+Y / Cmd+Y
        if (isRedoShortcut(e)) {
          e.preventDefault();
          if (canRedo()) {
            handleRedo();
          }
        }
      };

      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }, [
      isPropertyPanelOpen,
      canUndo,
      canRedo,
      selectedElement,
      pendingChanges,
    ]);

    // Helper function to filter out position style from element data
    const filterPositionStyle = (element: ElementData): ElementData => {
      if (!element.styles) return element;
      const { position, ...filteredStyles } = element.styles;
      return {
        ...element,
        styles: filteredStyles,
      };
    };

    // Handle element selection from preview
    const handleElementSelected = (elementData: ElementData) => {
      // Filter out position style from incoming element data (defense in depth)
      const filteredElementData = filterPositionStyle(elementData);

      setInternalSelectedElement(filteredElementData);
      setIsPropertyPanelOpen(true);
      // Initialize pending changes with EMPTY styles
      // IMPORTANT: Don't copy computed styles into pendingChanges
      // Only track actual user changes from this point forward
      const initialChanges = {
        text: filteredElementData?.content || "",
        styles: {}, // Start with empty - only accumulate actual changes
        attributes: {}, // Start with empty - only accumulate actual changes
      };
      setPendingChanges(initialChanges);
      // CRITICAL: Set styles to {} to avoid saving browser computed styles
      // We only want to track user changes from this point forward
      originalElementRef.current = {
        ...structuredClone(filteredElementData),
        styles: {}, // Empty styles - only track new changes
      };

      // Reset flag - initial state will be pushed on first change
      setHasInitialState(false);
    };

    const handleChangeContentCallback = (content: string) => {
      if (!selectedElement) return;

      const newPendingChanges = { ...pendingChanges, text: content };

      // Build the updated element (filter out position style to avoid layout issues)
      const updatedElement = {
        ...filterPositionStyle(selectedElement),
        content,
        text: content,
      };

      // Push initial state to history on first change (before the actual change)
      if (!hasInitialState && originalElementRef.current) {
        const filteredOriginal = filterPositionStyle(
          originalElementRef.current,
        );
        const initialChanges = {
          text: filteredOriginal.content || "",
          styles: filteredOriginal.styles || {},
          attributes: filteredOriginal.attributes || {},
        };
        pushHistory(
          originalElementRef.current.path,
          originalElementRef.current.path,
          initialChanges,
          filteredOriginal,
          getActionType(originalElementRef.current),
        );
        setHasInitialState(true);
      }

      // Update state
      setPendingChanges(newPendingChanges);

      // Push the actual change to history
      pushHistory(
        selectedElement.path,
        selectedElement.path,
        newPendingChanges,
        updatedElement,
        getActionType(selectedElement),
      );

      if (propertyPanelRef.current) {
        propertyPanelRef.current.updateContent(content);
      }
    };

    const handleChangeStylesCallback = (styles: Record<string, string>) => {
      if (!selectedElement) return;

      const newPendingChanges = {
        ...pendingChanges,
        styles: {
          ...pendingChanges.styles,
          ...styles,
        },
      };

      // Build the updated element (filter out position style)
      const updatedElement = {
        ...filterPositionStyle(selectedElement),
        styles: {
          ...filterPositionStyle(selectedElement).styles,
          ...styles,
        },
      };

      // Push initial state to history on first change (before the actual change)
      if (!hasInitialState && originalElementRef.current) {
        const initialChanges = {
          text: originalElementRef.current.content || "",
          className: originalElementRef.current.className || "",
          styles: originalElementRef.current.styles || {},
          attributes: originalElementRef.current.attributes || {},
        };
        pushHistory(
          originalElementRef.current.path,
          originalElementRef.current.path,
          initialChanges,
          originalElementRef.current,
          getActionType(originalElementRef.current),
        );
        setHasInitialState(true);
      }

      // Update state
      setPendingChanges(newPendingChanges);

      // Push the actual change to history
      pushHistory(
        selectedElement.path,
        selectedElement.path,
        newPendingChanges,
        updatedElement,
        getActionType(selectedElement),
      );

      if (propertyPanelRef.current) {
        propertyPanelRef.current.updateStyles(styles);
      }
    };

    const handlePropertyChange = (property: string, value: any) => {
      if (!selectedElement) return;

      // Calculate new pending changes FIRST
      let newPendingChanges = { ...pendingChanges };

      // Track changes
      if (property === "content") {
        newPendingChanges = { ...newPendingChanges, text: value };
      } else if (property === "className") {
        // Store className separately, not in attributes.class
        newPendingChanges = {
          ...newPendingChanges,
          className: value,
        };
      } else if (property === "attributes") {
        newPendingChanges = {
          ...newPendingChanges,
          attributes: {
            ...newPendingChanges.attributes,
            ...value,
          },
        };
      } else if (property === "batchStyles") {
        // Batch multiple style properties at once (e.g. spacing linked mode)
        newPendingChanges = {
          ...newPendingChanges,
          styles: {
            ...newPendingChanges.styles,
            ...value,
          },
        };
      } else {
        newPendingChanges = {
          ...newPendingChanges,
          styles: {
            ...newPendingChanges.styles,
            [property]: value,
          },
        };
      }

      // IMPORTANT: Only store user-modified styles, not all computed styles
      // This prevents saving hundreds of unnecessary style properties
      // Start with original element's styles (previously saved user changes)
      // Then add new pending changes (user modifications in this session)
      const updatedElementStyles = {
        ...(originalElementRef.current?.styles || {}),
        ...(newPendingChanges.styles || {}),
      };

      const updatedElement = {
        ...selectedElement,
        content:
          property === "content"
            ? value
            : newPendingChanges.text !== undefined
              ? newPendingChanges.text
              : selectedElement.content,
        className:
          property === "className"
            ? value
            : newPendingChanges.className !== undefined
              ? newPendingChanges.className
              : selectedElement.className,
        attributes: {
          ...selectedElement.attributes,
          ...(newPendingChanges.attributes || {}),
        },
        // Only user-modified styles, not browser computed styles
        styles: updatedElementStyles,
      };

      // Build element for preview iframe
      // Send all pending style changes (not just the current one) to preserve previous edits
      // For example: if user changed width, then color, we need to send both
      const resolvedClassName =
        newPendingChanges.className !== undefined
          ? newPendingChanges.className
          : selectedElement.className;

      const previewElement: ElementData = {
        ...selectedElement,
        path: selectedElement.path,
        tagName: selectedElement.tagName,
        content:
          newPendingChanges.text !== undefined
            ? newPendingChanges.text
            : selectedElement.content,
        className: resolvedClassName,
        attributes: {
          ...selectedElement.attributes,
          ...(newPendingChanges.attributes || {}),
          // Include className in attributes.class so the iframe handler applies it
          // The iframe's updateElementStyles processes attributes but not className directly
          ...(newPendingChanges.className !== undefined
            ? { class: newPendingChanges.className }
            : {}),
        },
        // Send only pending style changes, not all original computed styles
        styles: newPendingChanges.styles || {},
      };

      // Push initial state to history on first change (before the actual change)
      if (!hasInitialState && originalElementRef.current) {
        const initialChanges = {
          text: originalElementRef.current.content || "",
          className: originalElementRef.current.className || "",
          styles: originalElementRef.current.styles || {},
          attributes: originalElementRef.current.attributes || {},
        };
        pushHistory(
          originalElementRef.current.path,
          originalElementRef.current.path,
          initialChanges,
          originalElementRef.current,
          getActionType(originalElementRef.current),
        );
        setHasInitialState(true);
      }

      // Update state
      setPendingChanges(newPendingChanges);
      // Push the actual change to history
      pushHistory(
        selectedElement.path,
        selectedElement.path,
        newPendingChanges,
        updatedElement,
        getActionType(selectedElement),
      );
      setInternalSelectedElement(updatedElement);
      // Send only the changed property to iframe
      previewMessage.updateElement(previewRef?.current, previewElement);
    };

    const handleSave = async () => {
      if (!devContainerId) {
        toast({
          title: "Error",
          description: "Workspace not available",
          variant: "destructive",
        });
        return;
      }

      setIsSaving(true);
      try {
        const allChanges = getAllPendingChanges(history, currentIndex);
        console.log("[VisualEditor] Saving changes:", allChanges);
        if (allChanges.size === 0) {
          toast({
            title: "Info",
            description: "No changes to save",
          });
          setIsSaving(false);
          return;
        }

        // Collect all changes into separate arrays to control execution order
        // IMPORTANT: Style/text/attribute updates must happen BEFORE position changes
        // because position changes modify line numbers in the source file
        const styleTextUpdates: any[] = [];
        const positionUpdates: any[] = [];
        const otherUpdates: any[] = [];
        const elementsList: string[] = [];

        // Pre-scan: Build map of layout child IDs -> layout element path
        // This allows merging elements dropped into layout columns into the layout's children
        const layoutChildToParent = new Map<string, string>();
        const layoutElementData = new Map<string, ElementData>();
        allChanges.forEach(({ element, actionType }) => {
          if (
            actionType === "add" &&
            element.children &&
            element.children.length > 0
          ) {
            layoutElementData.set(element.path, element);
            for (const child of element.children) {
              const childId = child.id as string | undefined;
              if (childId) {
                layoutChildToParent.set(childId, element.path);
              }
            }
            console.log("[Save] Pre-scan: layout detected", {
              path: element.path,
              childIds: element.children.map((c) => c.id),
            });
          }
        });

        // Helper: build a child descriptor and merge it into a layout column's children array.
        // Returns true if merged successfully, false if the target column wasn't found.
        const mergeIntoLayoutChild = (
          columnId: string,
          element: ElementData,
          changes: HistorySnapshot["pendingChanges"],
        ): boolean => {
          const layoutPath = layoutChildToParent.get(columnId);
          if (!layoutPath) {
            console.warn(
              "[Save] mergeIntoLayoutChild: columnId not in layoutChildToParent",
              { columnId },
            );
            return false;
          }
          const layoutEl = layoutElementData.get(layoutPath);
          if (!layoutEl?.children) {
            console.warn(
              "[Save] mergeIntoLayoutChild: layout has no children",
              { layoutPath },
            );
            return false;
          }
          const targetChild = layoutEl.children.find(
            (c) => c.id === columnId,
          ) as Record<string, unknown> | undefined;
          if (!targetChild) {
            console.warn(
              "[Save] mergeIntoLayoutChild: column not found in layout children",
              {
                columnId,
                childIds: layoutEl.children.map((c) => c.id),
              },
            );
            return false;
          }
          if (!targetChild.children) targetChild.children = [];
          const rawAttrs = (changes.attributes ||
            element.attributes ||
            {}) as Record<string, string | boolean>;
          const innerAttrs = Object.fromEntries(
            Object.entries(rawAttrs).filter(
              ([, v]) => v !== false && typeof v === "string",
            ),
          );
          // Normalize styles for media elements inside layout columns
          const rawStyles = changes.styles || element.styles || {};
          const tag = (element.tagName || "div").toLowerCase();
          const isMedia = tag === "img" || tag === "video";
          const childStyle = isMedia
            ? { ...rawStyles, width: "100%", maxWidth: "100%", height: "auto" }
            : rawStyles;

          (targetChild.children as Array<Record<string, unknown>>).push({
            tagName: element.tagName || "div",
            id: element.path,
            style: childStyle,
            text: changes.text || element.content || "",
            className: element.className || undefined,
            attributes:
              Object.keys(innerAttrs).length > 0 ? innerAttrs : undefined,
            children: element.children || undefined,
            iconName: element.iconName || undefined,
          });
          console.log(
            "[Save] mergeIntoLayoutChild: merged element into column",
            {
              columnId,
              elementTag: element.tagName,
              childrenCount: (targetChild.children as unknown[]).length,
            },
          );
          return true;
        };

        // Pre-scan: collect section child paths so we can skip them as
        // independent commands. Their edits are captured in the parent
        // section's rawJsx (re-read from iframe DOM at save time).
        const sectionChildPaths = new Set<string>();
        const sectionParentIds = new Set<string>();
        allChanges.forEach(({ element }) => {
          if (isSectionChild(element.path)) {
            sectionChildPaths.add(element.path);
            const parentId = getSectionParentId(element.path);
            if (parentId) sectionParentIds.add(parentId);
          }
        });

        // Collect section child edits keyed by parent section ID.
        // These will be applied to the parent section's rawJsx string.
        const sectionChildEdits = new Map<
          string,
          Array<{ originalText: string; newText: string }>
        >();
        allChanges.forEach(({ element, changes, originalElement }) => {
          if (!isSectionChild(element.path)) return;
          const parentId = getSectionParentId(element.path);
          if (!parentId) return;

          const origText = originalElement?.content || element.content || "";
          const newText = changes.text;
          if (newText !== undefined && newText !== origText) {
            if (!sectionChildEdits.has(parentId)) {
              sectionChildEdits.set(parentId, []);
            }
            sectionChildEdits
              .get(parentId)
              ?.push({ originalText: origText, newText });
          }
        });

        allChanges.forEach(
          ({
            element,
            changes,
            originalElement,
            actionType,
            structureChange,
          }) => {
            // Skip section children — their edits are captured in the
            // parent section's rawJsx (re-read from iframe DOM below).
            if (sectionChildPaths.has(element.path)) {
              return;
            }

            // Handle "add" action first (early return to avoid treating it as a move)
            if (actionType === "add") {
              elementsList.push(element.path);

              // Merge into layout column if this element was dropped into one
              const parentId = structureChange?.targetElementId;
              if (parentId && layoutChildToParent.has(parentId)) {
                console.log("[Save] Attempting merge into layout column", {
                  parentId,
                  elementPath: element.path,
                  elementTag: element.tagName,
                });
                if (mergeIntoLayoutChild(parentId, element, changes)) {
                  return;
                }
                console.warn(
                  "[Save] Merge failed, falling through to add-element",
                );
              }

              const parentPath = structureChange?.parentAfter;
              let sourceFile = "";
              if (parentPath) {
                const match = parentPath.match(/^(\/[^:]+)/);
                if (match) {
                  sourceFile = match[1];
                }
              }

              // For add-element, filter out false/removed attributes —
              // they mean "attribute not set", so just exclude them entirely.
              // For add-element, filter out false attributes (means "not set")
              const rawAttrs = (changes.attributes ||
                element.attributes ||
                {}) as Record<string, string | boolean>;
              const cleanAttrs = Object.fromEntries(
                Object.entries(rawAttrs).filter(([, v]) => v !== false),
              );

              // If any section children were edited, apply text replacements
              // to the rawJsx so the saved template reflects the edits.
              let rawJsx = element.rawJsx;
              const childEdits = sectionChildEdits.get(element.path);
              if (rawJsx && childEdits && childEdits.length > 0) {
                for (const { originalText, newText } of childEdits) {
                  // Replace ">originalText<" pattern to target only text content
                  // within JSX tags, avoiding false matches on attribute values.
                  const escaped = originalText.replace(
                    /[.*+?^${}()|[\]\\]/g,
                    "\\$&",
                  );
                  rawJsx = rawJsx.replace(
                    new RegExp(`(>)${escaped}(<)`, "g"),
                    `$1${newText}$2`,
                  );
                }
              }

              const resolvedParent = structureChange?.targetElementId || null;

              // Skip add-element with no resolvable parent and no source file —
              // backend cannot process these (no file path to write to)
              if (!resolvedParent && !sourceFile) {
                console.warn(
                  "[Save] Skipping add-element with no parent and no source",
                  { path: element.path, tag: element.tagName },
                );
                return;
              }

              const addCmd = {
                command: "add-element",
                parentElementPath: resolvedParent,
                elementType: element.tagName,
                text: changes.text || element.content,
                classNames: element.className,
                styleUpdates: changes.styles || element.styles,
                position: structureChange?.dropPosition || "bottom",
                attributes: cleanAttrs,
                sourceFile: sourceFile,
                children: element.children,
                ...(rawJsx && { rawJsx }),
                ...(element.iconName && { iconName: element.iconName }),
                // For duplicates: skip section walk-up logic; targetElementId points to the
                // original element so rawJsx inserts as its sibling (not inside it)
                ...(structureChange?.isDuplicate && { insertAsChild: true }),
              };
              console.log("[Save] add-element command", {
                path: element.path,
                elementType: element.tagName,
                hasChildren: !!(
                  element.children && element.children.length > 0
                ),
                childrenSummary: element.children?.map(
                  (c: Record<string, unknown>) => ({
                    id: c.id,
                    tag: c.tagName,
                    nestedCount: Array.isArray(c.children)
                      ? (c.children as unknown[]).length
                      : 0,
                  }),
                ),
              });
              otherUpdates.push(addCmd);
              return;
            }

            // Handle "delete" action (early return)
            if (actionType === "delete") {
              // Skip remove-element for unsaved generated-ID elements (layout children, temp elements)
              // These only exist in iframe DOM, never persisted to source files — nothing to remove
              if (
                (element.source?.fileName &&
                  isGeneratedId(element.source.fileName)) ||
                isGeneratedId(element.path)
              ) {
                elementsList.push(element.path);
                return;
              }

              if (!element.source) {
                console.warn("Delete action missing source location");
                return;
              }
              elementsList.push(element.path);
              otherUpdates.push({
                command: "remove-element",
                elementPath: element.path,
                bind: originalElement?.bind,
                source: element.source,
              });
              return;
            }

            // IMPORTANT: Check for structureChange (move operation) for update/move actions only
            // This handles cases where: move first → style update (actionType="update" but structureChange exists)
            // Check targetElementId as well — parentAfter can be null for sibling moves
            if (
              structureChange &&
              (structureChange.parentAfter || structureChange.targetElementId)
            ) {
              const targetId = structureChange.targetElementId || "";

              // Merge into layout column if target is a generated ID (not a real file path)
              if (
                isGeneratedId(targetId) &&
                layoutChildToParent.has(targetId)
              ) {
                if (mergeIntoLayoutChild(targetId, element, changes)) {
                  if (element.source) {
                    otherUpdates.push({
                      command: "remove-element",
                      elementPath: element.path,
                      bind: originalElement?.bind,
                      source: element.source,
                    });
                  }
                  if (!elementsList.includes(element.path)) {
                    elementsList.push(element.path);
                  }
                  return;
                }
              }

              // Normal update-position for targets with real data-source (file:line:col)
              const targetSource = parseSourceLocation(targetId);

              const source = element.source;
              if (!source) {
                console.warn(
                  "Move operation missing source location for",
                  element.path,
                );
                return;
              }

              // Add to positionUpdates array (will be added to batch AFTER style updates)
              positionUpdates.push({
                command: "update-position",
                elementPath: element.path,
                bind: originalElement?.bind,
                position: structureChange.dropPosition,
                source: {
                  fileName: source.fileName,
                  lineNumber: source.lineNumber,
                  columnNumber: source.columnNumber,
                },
                targetSource: {
                  fileName: targetSource?.fileName + "",
                  lineNumber: Number.parseInt(targetSource?.lineNumber + ""),
                  columnNumber: Number.parseInt(
                    targetSource?.columnNumber + "",
                  ),
                },
              });

              if (!elementsList.includes(element.path)) {
                elementsList.push(element.path);
              }
              // IMPORTANT: Don't return - fall through to process style/text/attribute updates
              // This ensures that if the element was both moved AND styled, both changes are included
            }

            // At this point, actionType is either "update" or "move"
            // Both need style/text/attribute processing, so continue below

            // Filter out text/content if it hasn't changed from original
            // Use ?? to avoid empty string "" being treated as falsy
            const originalContent =
              originalElement?.content ??
              originalElement?.text ??
              element.content;

            // Only include text if it has changed
            const newContent = changes.text;
            const hasTextChange =
              newContent !== undefined && newContent !== originalContent;

            // Only include styles that have actually changed
            const changedStyles: Record<string, string> = {};
            if (changes.styles && Object.keys(changes.styles).length > 0) {
              const originalStyles =
                originalElement?.styles || element.styles || {};
              for (const [key, value] of Object.entries(changes.styles)) {
                if (originalStyles[key] !== value) {
                  changedStyles[key] = value;
                }
              }
            }

            // Only include attributes that have actually changed
            const changedAttrs: Record<string, string> = {};
            if (
              changes.attributes &&
              Object.keys(changes.attributes).length > 0
            ) {
              const originalAttrs =
                originalElement?.attributes || element.attributes || {};
              for (const [key, value] of Object.entries(changes.attributes)) {
                if (originalAttrs[key] !== value) {
                  changedAttrs[key] = value;
                }
              }
            }

            // Check if className has changed
            // Use nullish coalescing (??) to avoid empty string "" being treated as falsy
            const originalClassName =
              originalElement?.className ?? element.className ?? "";
            const newClassName = changes.className;
            const hasClassNameChange =
              newClassName !== undefined && newClassName !== originalClassName;

            // If className changed, add to changedAttrs as 'class' for backend compatibility
            if (hasClassNameChange) {
              changedAttrs.class = newClassName;
            }

            // Skip if no real changes (unless it's a move-only action, already handled above)
            if (
              !hasTextChange &&
              Object.keys(changedStyles).length === 0 &&
              Object.keys(changedAttrs).length === 0
            ) {
              return;
            }

            // If this element is a layout child (column/row), merge changes into
            // the layout's children definition instead of sending separate commands.
            // Layout children have temp IDs (optidev-col-*) as source, not file paths.
            if (layoutChildToParent.has(element.path)) {
              const layoutPath = layoutChildToParent.get(element.path);
              const layoutEl = layoutPath
                ? layoutElementData.get(layoutPath)
                : undefined;
              if (layoutEl?.children) {
                const targetChild = layoutEl.children.find(
                  (c) => c.id === element.path,
                ) as Record<string, unknown> | undefined;
                if (targetChild) {
                  // Merge style changes into the child definition
                  if (Object.keys(changedStyles).length > 0) {
                    targetChild.style = {
                      ...((targetChild.style as Record<string, string>) || {}),
                      ...changedStyles,
                    };
                  }
                  if (hasTextChange) {
                    targetChild.text = newContent;
                  }
                  if (hasClassNameChange) {
                    targetChild.className = newClassName;
                  }
                  return; // Skip separate style/text/attribute commands
                }
              }
            }

            // Add to elementsList only if not already added by move action
            if (!elementsList.includes(element.path)) {
              elementsList.push(element.path);
            }

            // Create change entries - add to styleTextUpdates array (executed before position changes)
            if (hasTextChange) {
              styleTextUpdates.push({
                command: "update-text",
                elementPath: element.path,
                bind: originalElement?.bind,
                text: newContent,
                source: element.source,
              });
            }

            if (Object.keys(changedStyles).length > 0 || changedAttrs.class) {
              styleTextUpdates.push({
                command: "update-style",
                elementPath: element.path,
                bind: originalElement?.bind,
                styleUpdates: changedStyles,
                classUpdates: changedAttrs.class,
                source: element.source,
              });
            }

            // Send attribute updates as separate command (excluding 'class' which is handled above)
            const nonClassAttrs = { ...changedAttrs };
            delete nonClassAttrs.class;
            delete nonClassAttrs.style;
            if (Object.keys(nonClassAttrs).length > 0) {
              styleTextUpdates.push({
                command: "update-attributes",
                elementPath: element.path,
                bind: originalElement?.bind,
                attributeUpdates: nonClassAttrs,
                source: element.source,
              });
            }
          },
        );

        // Combine all updates in the correct order:
        // 1. Style/text/attribute updates (must happen first - use old line numbers)
        // 3. Other updates (add/delete)
        const batchVisualEdits = [
          ...styleTextUpdates,
          ...positionUpdates,
          ...otherUpdates,
        ];

        if (batchVisualEdits.length === 0) {
          toast({
            title: "Info",
            description: "No meaningful changes to save",
          });
          setIsSaving(false);
          return;
        }

        const hasAddElement = batchVisualEdits.some(
          (edit) => edit.command === "add-element",
        );
        const hasUpdatePosition = batchVisualEdits.some(
          (edit) => edit.command === "update-position",
        );
        const hasAutoSaveAdd = batchVisualEdits.some(
          (edit) =>
            edit.command === "add-element" &&
            ((edit as Record<string, unknown>).iconName ||
              (edit as Record<string, unknown>).rawJsx),
        );
        const hasRemoveElement = batchVisualEdits.some(
          (edit) => edit.command === "remove-element",
        );

        // Undo DOM moves before save to prevent React reconciliation errors
        if (hasUpdatePosition && previewRef?.current) {
          const moveOperations = Array.from(allChanges.values())
            .filter(
              (change) =>
                change.structureChange?.parentBefore !== null &&
                change.structureChange?.parentBefore !== undefined,
            )
            .map((change) => ({
              elementPath: change.element.path,
              parentBefore: change.structureChange!.parentBefore!,
              positionBefore: change.structureChange!.positionBefore ?? null,
            }));

          if (moveOperations.length > 0) {
            previewMessage.send(previewRef.current, {
              type: PreviewMessageType.UNDO_DOM_MOVES,
              moves: moveOperations,
            });
            await new Promise((resolve) => setTimeout(resolve, 10));
          }
        }

        const result = await visualEditorService.batchVisualEdit(
          batchVisualEdits,
          devContainerId,
        );
        const filesModified = result.filesModified || [];
        const successCount =
          result.results?.filter((r: any) => r.success).length || 0;
        const totalCount = result.results?.length || 0;

        if (result.success) {
          markFirstEdit();

          if (previewRef?.current) {
            previewMessage.clearSelection(previewRef.current);
          }

          // Clean up DOM before HMR: remove temp elements and restore soft-deleted
          // visibility so React doesn't reuse hidden DOM nodes during reconciliation
          if (previewRef?.current?.contentWindow) {
            const addedElementPaths: string[] = [];
            const deletedElementPaths: string[] = [];
            allChanges.forEach((changeData, path) => {
              if (changeData.actionType === "add") {
                addedElementPaths.push(path);
              } else if (changeData.actionType === "delete") {
                deletedElementPaths.push(path);
              }
            });

            if (
              addedElementPaths.length > 0 ||
              deletedElementPaths.length > 0
            ) {
              previewMessage.send(previewRef.current, {
                type: PreviewMessageType.CLEANUP_TEMP_ELEMENTS,
                elementPaths: addedElementPaths,
                deletedElementPaths,
              });
            }
          }

          // Skip commit for auto-saved adds (icons, sections/navs) — they
          // save on drop and will be included in the next regular commit.
          if (projectId && !hasAutoSaveAdd) {
            try {
              const token = await auth.getIdToken();
              const API_URL = import.meta.env.VITE_API_URL || "";

              let commitMessage = `Visual changes: Updated ${elementsList.length} element(s)`;
              // Step 1: Generate AI commit message with detailed changes
              // Build detailed context with actual change values
              const changesDetails = Array.from(allChanges.values())
                .map((change) => {
                  const parts: string[] = [];

                  // Text changes with actual content
                  if (change.changes?.text !== undefined) {
                    const text =
                      change.changes.text.length > 40
                        ? change.changes.text.substring(0, 40) + "..."
                        : change.changes.text;
                    parts.push(`text: "${text}"`);
                  }

                  // Style changes with actual values
                  if (change.changes?.styles) {
                    const styles = Object.entries(change.changes.styles)
                      .map(
                        ([k, v]) =>
                          `${k}: ${String(v).length > 25 ? String(v).substring(0, 25) + "..." : v}`,
                      )
                      .join(", ");
                    if (styles) parts.push(`styles [${styles}]`);
                  }

                  if (change.actionType === "move") {
                    parts.push("repositioned");
                  }

                  return parts.length > 0
                    ? `- ${change.element.path}: ${parts.join("; ")}`
                    : null;
                })
                .filter(Boolean)
                .join("\n");

              try {
                // Notify parent: Starting commit generation
                onCommitStateChange?.("generating");

                const generateResponse = await axios.post(
                  `${API_URL}/api/projects/${projectId}/commits/generate-message`,
                  {
                    changedFiles:
                      filesModified.length > 0
                        ? filesModified
                        : ["visual-editor"],
                    additionalContext: changesDetails
                      ? `Visual editor changes:\n${changesDetails}`
                      : `Updated ${elementsList.length} element(s) in visual editor`,
                    devContainerId,
                  },
                  {
                    headers: {
                      Authorization: token ? `Bearer ${token}` : "",
                    },
                  },
                );
                commitMessage = generateResponse.data.message;
              } catch (aiError: any) {
                console.warn(
                  "Failed to generate AI commit message, using fallback:",
                  aiError,
                );
              }

              // Step 2: Create commit with generated message
              // Notify parent: Starting commit creation
              onCommitStateChange?.("creating");

              const response = await axios.post<Commit>(
                `${API_URL}/api/projects/${projectId}/commits`,
                {
                  message: commitMessage,
                },
                {
                  headers: {
                    Authorization: token ? `Bearer ${token}` : "",
                  },
                },
              );
              // NEW: Update store to trigger sync changes detection in PublishDropdown
              if (response.data && response.data.commitSha) {
                setAutoCommit({
                  commitId: response.data.id,
                  commitSha: response.data.commitSha,
                  message: response.data.message,
                  filesCount: response.data.metadata?.changedFiles || 0,
                  status: "created",
                  timestamp: Date.now(),
                });
              }

              // Notify parent: Commit process completed
              onCommitStateChange?.("idle");

              toast({
                title: "Changes applied",
                description: `${elementsList.length} element${elementsList.length > 1 ? "s" : ""} saved to ${filesModified.join(", ") || "source files"}`,
                variant: "success",
              });
            } catch (commitError: any) {
              // Reset commit state on error
              onCommitStateChange?.("idle");

              // Don't fail the save if commit fails, just log it
              console.warn(
                "Failed to auto-create commit:",
                commitError.response?.data?.message || commitError.message,
              );
            }
          }

          // Clear history after successful save - complete reset
          clearHistory();

          // Request slide thumbnail regeneration only for changed slides
          const slideState = useSlideStore.getState();
          if (slideState.isSlideProject && slideState.slides.length > 0) {
            if (result.slideThumbnailsUpdated && devContainerId) {
              // Server captured thumbnails and updated slides.json — re-fetch
              // the manifest so each slide's `thumbnail` field picks up the
              // new UUID filename. The per-slide `useSlideThumbnail` hook
              // keys on that path and re-fetches the blob automatically.
              useSlideStore.getState().bumpThumbnailRevision();
              fetchSlideManifest({
                devContainerId,
                getIdToken: () => auth.getIdToken(),
              }).catch(() => {
                /* non-blocking */
              });
            }
          }

          // Clear visual editor state completely after save
          setIsPropertyPanelOpen(false);
          setInternalSelectedElement(null);
          setPendingChanges({});

          // Notify parent to clear external selected element and toolbar
          if (onElementDeselect) {
            onElementDeselect();
          }

          // Force iframe reload for adds (new imports HMR can't resolve) and
          // deletes (soft-deleted display:none persists through HMR reconciliation).
          // IMPORTANT: Read previewRef.current INSIDE the timeout — React re-renders
          // from state clearing above may replace the iframe DOM element.
          if (hasAutoSaveAdd || hasRemoveElement) {
            setTimeout(() => {
              try {
                const iframe = previewRef?.current;
                if (iframe?.contentWindow) {
                  iframe.contentWindow.location.reload();
                }
              } catch {
                // iframe might have been unmounted
              }
            }, 1200);
          }
          // Style/text updates are handled by HMR
        } else {
          const failedResults =
            result.results?.filter((r: any) => !r.success) || [];
          const errorMessages = failedResults
            .map((r: any) => r.error)
            .join(", ");
          throw new Error(
            `Failed to save some changes (${successCount}/${totalCount} succeeded): ${errorMessages || result.error}`,
          );
        }
      } catch (error) {
        console.error("Failed to save:", error);
        toast({
          title: "Error",
          description:
            error instanceof Error ? error.message : "Failed to save changes",
          variant: "destructive",
        });
      } finally {
        setIsSaving(false);
      }
    };

    const handleDiscard = () => {
      const iframe = previewRef?.current;

      // Clear local state first to prevent any re-selection
      setIsPropertyPanelOpen(false);
      setInternalSelectedElement(null);
      setPendingChanges({});

      // Notify parent to clear external selected element and toolbar
      if (onElementDeselect) {
        onElementDeselect();
      }

      // Discard all changes by reverting to original state
      if (iframe?.contentWindow && history.length > 0) {
        // Get relevant history (up to currentIndex)
        const relevantHistory = history.slice(0, currentIndex + 1);

        // Separate move operations from other operations
        const moveOperations: any[] = [];
        const otherOperations = new Map<string, any>();

        for (const snapshot of relevantHistory) {
          if (snapshot.actionType === "move") {
            moveOperations.push(snapshot);
          } else if (!otherOperations.has(snapshot.elementId)) {
            // Store FIRST (oldest) snapshot for each element
            // This represents the original state before any changes
            otherOperations.set(snapshot.elementId, snapshot);
          }
        }

        // Step 1: Undo ALL move operations in REVERSE order
        // This ensures elements return to their original positions
        // even if the same element was moved multiple times
        for (let i = moveOperations.length - 1; i >= 0; i--) {
          handleUndoRedoAction(moveOperations[i], "undo");
        }

        // Step 2: Handle other operations (add/delete/update)
        for (const [, firstSnapshot] of otherOperations.entries()) {
          const actionType = firstSnapshot.actionType;

          if (actionType === "add" && firstSnapshot.isInitialAdd) {
            // For add: use handleUndoRedoAction to properly remove element
            // This sends UNDO_ADD message which handles DOM tree removal correctly
            handleUndoRedoAction(firstSnapshot, "undo");
          } else if (actionType === "delete") {
            // For delete: use handleUndoRedoAction to restore element
            // This sends UNDO_DELETE message which handles DOM tree restoration
            handleUndoRedoAction(firstSnapshot, "undo");
          } else if (actionType === "update" || !firstSnapshot.isInitialAdd) {
            // For update: restore to ORIGINAL state (before any changes)
            // We use originalElementData (the initial state) not fullElementData
            const originalElement =
              firstSnapshot.originalElementData ||
              firstSnapshot.fullElementData;

            if (originalElement) {
              previewMessage.updateElement(iframe, originalElement);
            }
          }
        }
      }

      // Clear history after all operations
      clearHistory();

      // Clear selection outline in preview iframe
      // Do this AFTER all updates to ensure any selection created during updates is removed
      previewMessage.clearSelection(previewRef?.current);
    };

    const handleLayersUpdate = (layersData: any[]) => {
      setLayers(layersData);
    };

    // Helper function to handle undo/redo for different action types
    const handleUndoRedoAction = (
      snapshot: {
        actionType: "update" | "add" | "delete" | "move";
        isInitialAdd?: boolean;
        elementId: string;
        elementSelector: string;
        pendingChanges: {
          text?: string;
          styles?: Record<string, string>;
          attributes?: Record<string, string>;
        };
        fullElementData: ElementData;
        structureChange?: {
          parentBefore?: string | null;
          parentAfter?: string | null;
          positionBefore?: number | string | null;
          positionAfter?: number | string | null;
        };
      },
      mode: "undo" | "redo",
    ) => {
      const iframe = previewRef?.current;
      if (!iframe?.contentWindow) return;

      switch (snapshot.actionType) {
        case "update":
          // Update element properties (styles, content, attributes)
          console.log(
            "[VisualEditor] handleUndoRedoAction update:",
            mode,
            snapshot.fullElementData,
          );
          previewMessage.styleUpdateHistory(iframe, snapshot.fullElementData);
          // Update internal state and PropertyPanel
          updateElementState(snapshot.fullElementData, snapshot.pendingChanges);
          break;

        case "add":
          // Only handle structural add/remove for isInitialAdd snapshots
          // For subsequent updates, treat as normal property updates
          if (snapshot.isInitialAdd) {
            // Undo add = delete, Redo add = re-add
            previewMessage.send(iframe, {
              type:
                mode === "undo"
                  ? PreviewMessageType.UNDO_ADD
                  : PreviewMessageType.REDO_ADD,
              element: snapshot.fullElementData,
              elementPath: snapshot.elementSelector,
              parentId:
                mode === "undo"
                  ? snapshot.structureChange?.parentAfter
                  : snapshot.structureChange?.parentAfter,
              position: snapshot.structureChange?.positionAfter ?? null,
            });

            // For redo, also update internal state and PropertyPanel
            if (mode === "redo") {
              updateElementState(
                snapshot.fullElementData,
                snapshot.pendingChanges,
              );
            }
          } else {
            // This is a subsequent update to an added element
            // Treat it like a normal update (no structural change)
            previewMessage.updateElement(iframe, snapshot.fullElementData);
            // Update internal state and PropertyPanel
            updateElementState(
              snapshot.fullElementData,
              snapshot.pendingChanges,
            );
          }
          break;

        case "delete":
          // Undo delete = restore, Redo delete = remove again
          previewMessage.send(iframe, {
            type:
              mode === "undo"
                ? PreviewMessageType.UNDO_DELETE
                : PreviewMessageType.REDO_DELETE,
            element: snapshot.fullElementData,
            elementPath: snapshot.elementSelector,
            parentId:
              mode === "undo"
                ? snapshot.structureChange?.parentBefore
                : snapshot.structureChange?.parentBefore,
            position: snapshot.structureChange?.positionBefore ?? null,
          });
          break;

        case "move":
          // Move element to previous or next position
          previewMessage.send(iframe, {
            type: PreviewMessageType.MOVE_ELEMENT,
            elementId: snapshot.elementId,
            elementPath: snapshot.elementSelector,
            parentId:
              mode === "undo"
                ? snapshot.structureChange?.parentBefore
                : snapshot.structureChange?.parentAfter,
            position:
              mode === "undo"
                ? snapshot.structureChange?.positionBefore
                : snapshot.structureChange?.positionAfter,
          });
          break;
      }
    };

    // Handle undo - support all action types (update, add, delete, move)
    const handleUndo = () => {
      const state = useVisualEditorHistoryStore.getState();

      // For move, delete, and INITIAL add actions, we need to access the CURRENT snapshot
      // This is different from update actions where we go to previous snapshot
      const currentSnapshot = state.history[state.currentIndex];
      const currentAction = currentSnapshot?.actionType;
      const isInitialAdd = currentSnapshot?.isInitialAdd;

      if (
        state.currentIndex >= 0 &&
        (currentAction === "move" ||
          currentAction === "delete" ||
          (currentAction === "add" && isInitialAdd === true))
      ) {
        // Batch undo: if this snapshot has a batchId, undo ALL consecutive snapshots with the same batchId
        const batchId = currentSnapshot.batchId;
        if (batchId) {
          // Collect all consecutive snapshots with the same batchId going backwards
          let idx = state.currentIndex;
          const batchSnapshots: typeof state.history = [];
          while (idx >= 0 && state.history[idx]?.batchId === batchId) {
            batchSnapshots.unshift(state.history[idx]!);
            idx--;
          }

          // Apply undo for each snapshot in the batch (in reverse order)
          for (const snapshot of batchSnapshots) {
            handleUndoRedoAction(snapshot, "undo");
          }

          // Move index past the entire batch
          useVisualEditorHistoryStore.setState({
            currentIndex: idx,
          });
          return;
        }

        // Single undo (no batchId)
        handleUndoRedoAction(currentSnapshot, "undo");

        // Always decrease currentIndex for these actions (even from 0 to -1)
        // This allows redo to work properly
        const newIndex = state.currentIndex - 1;
        useVisualEditorHistoryStore.setState({
          currentIndex: newIndex,
        });
        return;
      }

      // For other action types (including subsequent 'add' updates), use normal undo
      const previousSnapshot = undo();
      if (!previousSnapshot) return;
      handleUndoRedoAction(previousSnapshot, "undo");
    };

    // Handle redo - support all action types (update, add, delete, move)
    const handleRedo = () => {
      const state = useVisualEditorHistoryStore.getState();

      // Check if we can redo
      if (!state.canRedo()) return;

      // For move, delete, and INITIAL add actions, we need to access the NEXT snapshot
      const nextIndex = state.currentIndex + 1;
      const nextSnapshot = state.history[nextIndex];
      const isInitialAdd = nextSnapshot?.isInitialAdd;

      if (
        nextSnapshot?.actionType === "move" ||
        nextSnapshot?.actionType === "delete" ||
        (nextSnapshot?.actionType === "add" && isInitialAdd === true)
      ) {
        // Batch redo: if this snapshot has a batchId, redo ALL consecutive snapshots with the same batchId
        const batchId = nextSnapshot.batchId;
        if (batchId) {
          let idx = nextIndex;
          const batchSnapshots: typeof state.history = [];
          while (
            idx < state.history.length &&
            state.history[idx]?.batchId === batchId
          ) {
            batchSnapshots.push(state.history[idx]!);
            idx++;
          }

          // Apply redo for each snapshot in the batch (in forward order)
          for (const snapshot of batchSnapshots) {
            handleUndoRedoAction(snapshot, "redo");
          }

          // Move index to end of batch
          useVisualEditorHistoryStore.setState({
            currentIndex: idx - 1,
          });
          return;
        }

        // Single redo (no batchId)
        handleUndoRedoAction(nextSnapshot, "redo");

        // Move index forward
        redo(); // This will increase currentIndex
        return;
      }

      // For other action types (including subsequent 'add' updates), use normal redo
      const redoSnapshot = redo();
      if (!redoSnapshot) return;
      handleUndoRedoAction(redoSnapshot, "redo");
    };

    const handleElementSelectPreview = (elementId: string) => {
      // Enable selection mode if not already enabled
      if (!isSelectionEnabled && onToggleSelection) {
        onToggleSelection(true);
      }

      previewMessage.selectByPath(previewRef?.current, elementId);
    };

    const handleElementHoverPreview = (elementId: string | null) => {
      previewMessage.hoverByPath(previewRef?.current, elementId);
    };

    const handleElementReorderPreview = (from: string, to: string) => {
      // Find the element being moved in layers to get its current parent info
      const fromElementInfo = findElementInLayers(layers, from);

      // Send reorder message with element data for history tracking
      previewMessage.send(previewRef?.current, {
        type: PreviewMessageType.REORDER_ELEMENTS,
        from,
        to,
        oldParentId: fromElementInfo?.parent?.id || null,
        oldPosition: fromElementInfo?.index ?? null,
        // Include element data for history
        elementData: fromElementInfo?.element
          ? {
              tagName: fromElementInfo.element.tagName,
              text:
                fromElementInfo.element.text || fromElementInfo.element.content,
              className: fromElementInfo.element.className,
              // Note: layers don't have styles/attributes, they will be retrieved from DOM
            }
          : null,
      });

      // Request updated layers after reorder to reflect new structure
      setTimeout(() => {
        previewMessage.requestLayers(previewRef?.current);
      }, LAYERS_UPDATE_DELAY);
    };

    // Expose methods to parent component
    useImperativeHandle(
      ref,
      () => ({
        handleElementSelected,
        handleChangeContentCallback,
        handleChangeStylesCallback,
        handleUndo,
        handleRedo,
        canUndo,
        canRedo,
        handleLayersUpdate,
        handleSave,
        handleDiscard,
      }),
      [
        handleElementSelected,
        handleChangeContentCallback,
        handleChangeStylesCallback,
        handleUndo,
        handleRedo,
        handleLayersUpdate,
        handleSave,
        handleDiscard,
      ],
    );

    // Request layers when component mounts (if visual mode is already active on page load)
    useEffect(() => {
      if (previewRef?.current) {
        previewMessage.requestLayers(previewRef.current);
      }
    }, []);

    return (
      <div className="flex flex-col h-full bg-background">
        {/* Main Content */}
        <div className="flex-1 flex flex-col ">
          {/* Tabs for Property and Insert */}
          <Tabs
            value={activeTab}
            onValueChange={(value) => setActiveTab(value as VisualEditorTab)}
            className="flex-1 flex flex-col bg-background min-h-0"
          >
            <TabsContent
              value="property"
              className="flex-1 overflow-auto min-h-0 mt-0"
            >
              {multiSelectCount && multiSelectCount > 1 ? (
                <div className="flex flex-col items-center justify-center h-full gap-2 p-6 text-center">
                  <p className="text-sm font-medium text-foreground">
                    {multiSelectCount} elements selected
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Press Delete to remove selected elements.
                    <br />
                    Click a single element to edit its properties.
                  </p>
                </div>
              ) : (
                <PropertyPanel
                  ref={propertyPanelRef}
                  element={selectedElement}
                  onChange={canEditFiles ? handlePropertyChange : undefined}
                  onSave={canEditFiles ? handleSave : undefined}
                  onDiscard={canEditFiles ? handleDiscard : undefined}
                  isSaving={isSaving}
                  previewRef={previewRef}
                  readOnly={!canEditFiles}
                  onAiCommand={onAiCommand}
                />
              )}
            </TabsContent>

            <TabsContent
              value="layers"
              className="flex-1 overflow-auto min-h-0 mt-0"
            >
              <LayersPanel
                layers={layers}
                selectedElementPath={selectedElement?.path}
                onElementSelect={(elementId) =>
                  handleElementSelectPreview(elementId)
                }
                onElementHover={(elementId) =>
                  handleElementHoverPreview(elementId)
                }
                onReorder={
                  canEditFiles
                    ? (from, to) => handleElementReorderPreview(from, to)
                    : undefined
                }
                readOnly={!canEditFiles}
              />
            </TabsContent>

            <TabsContent
              value="insert"
              className="flex-1 overflow-auto min-h-0 mt-0"
            >
              {canEditFiles ? (
                <InsertPanel />
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center p-4">
                  <Eye className="w-8 h-8 text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">
                    View only mode - inserting elements is disabled
                  </p>
                </div>
              )}
            </TabsContent>

            <TabsContent
              value="pages"
              className="flex-1 overflow-auto min-h-0 mt-0"
            >
              {devContainerId ? (
                <PagesPanel
                  devContainerId={devContainerId}
                  currentPath={currentPath}
                  onNavigate={(route) => {
                    previewMessage.navigateToRoute(previewRef?.current, route);
                    onPathChange?.(route);
                  }}
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center p-4">
                  <p className="text-sm text-muted-foreground">
                    No dev container connected
                  </p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    );
  },
);

VisualEditor.displayName = "VisualEditor";

export default VisualEditor;
